import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api, type CompStage, type DivisionScoreboardEntry } from '../lib/api';
import { HelpText } from './HelpText';
import { StandingsTable } from './StandingsTable';

// ─────────────────────────────────────────────────────────────────────────────
// DivisionsScoreboardPanel — R4 (comp-lifecycle-pipeline): once a LIVE
// DIVISIONS stage has generated its per-division fixtures, this renders every
// division's STANDINGS. Mirrors SeedingRoundsPanel (R2)'s round-tabs idiom
// only in spirit — the standings table itself is StandingsTable (extracted
// FROM SeedingRoundsPanel for this — same Pos/Team/P/W/L/Pts visual, this is
// the only caller passing `advancesCount` since only a DIVISIONS stage's
// config carries an advancePerGroup number).
//
// Info-architecture split (Lisa, 2026-07-18): "Divisions is for RESULTS,
// Matches is to CREATE and VIEW matches (incl. scheduling)". This panel used
// to ALSO carry the per-division weekly fixtures table (who-vs-who, inline
// result entry, the propose/agree date control) and a per-captain "Your
// Schedule" block. Both are gone now:
//   - The fixtures table + inline result entry + date-agreement control moved
//     onto the match card itself (MatchCardLive.tsx renders
//     MatchDateAgreementControl for a division match directly — see that
//     file). Divisions never showed a way to ENTER a match from here going
//     forward; that's Matches' job.
//   - "Your Schedule" was a straight DUPLICATE of pages/Calendar.tsx's own
//     "Your Calendar" per-captain upcoming-match view — deleted outright,
//     not moved, since Calendar already owns that job.
// This panel is standings-only from here on: a division name pill you open
// to see its table, nothing else.
// ─────────────────────────────────────────────────────────────────────────────

export function DivisionsScoreboardPanel({ compId, stage }: { compId: string; stage: CompStage }) {
  const stageId = stage.stageId;
  // Same fallback default generateDivisionsPlay itself uses server-side
  // (index.ts: `Number(target.config.advancePerGroup) || 4`) — keeps the
  // "who advances" marker honest even for a stage confirmed before
  // advancePerGroup was explicitly set.
  const advancePerGroup = Number(stage.config.advancePerGroup) || 4;

  const scoreboardKey = ['divisionsScoreboard', compId, stageId];
  const { data: scoreboard } = useQuery({
    queryKey: scoreboardKey,
    queryFn: () => api.getDivisionsScoreboard(compId, stageId),
    enabled: !!compId && !!stageId,
  });

  if (!scoreboard || scoreboard.divisions.length === 0) return null;

  return (
    <div className="mt-3 border-t border-white/5 pt-5">
      <p className="mb-2 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">Division Standings</p>
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">
          Each division plays its own round robin, scored on its own; the top {advancePerGroup} in every
          division advance once this stage ends.
        </p>
      </HelpText>

      <div className="space-y-2">
        {scoreboard.divisions.map((division) => (
          <DivisionCard key={division.groupIndex} division={division} advancePerGroup={advancePerGroup} />
        ))}
      </div>
    </div>
  );
}

// ── DivisionCard — one division as a collapsible accent "pill" (Lisa's
// mockup, 2026-07-18): collapsed shows just a full-width filled accent bar
// labelled with the division name; open reveals that division's
// StandingsTable underneath on the normal dark panel background. Extracted
// so the list `.map()` above never puts a hook in a loop — its own open/
// closed boolean is the only state here.
function DivisionCard({ division, advancePerGroup }: { division: DivisionScoreboardEntry; advancePerGroup: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-2xl bg-accent-500 px-4 py-3 font-display font-black uppercase tracking-wide text-[#0a0a0c] transition-colors hover:bg-accent-400"
      >
        <span className="truncate">{division.name}</span>
        {open
          ? <ChevronDown className="h-4 w-4 shrink-0" />
          : <ChevronRight className="h-4 w-4 shrink-0" />}
      </button>

      {open && (
        <div className="mt-1.5 rounded-lg bg-white/[0.03] p-3">
          <StandingsTable standings={division.standings} advancesCount={advancePerGroup} />
        </div>
      )}
    </div>
  );
}
